import React, { useState, useEffect } from 'react';
import theme from '../../../theme/globalTheme';
import { 
  Table, 
  Button, 
  Form, 
  Input, 
  DatePicker, 
  TimePicker, 
  Modal, 
  Space, 
  Card, 
  message,
  Popconfirm,
  Drawer 
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined } from '@ant-design/icons';
import { supabase }  from '../../../api/supabase';
import dayjs from 'dayjs';

const LunchOrdersPage = () => {
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [isAddDrawerOpen, setIsAddDrawerOpen] = useState(false);
  const [isViewDrawerOpen, setIsViewDrawerOpen] = useState(false);
  const [currentOrder, setCurrentOrder] = useState(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    sortField: 'date_of_charter',
    sortDirection: 'descend'
  });

  useEffect(() => {
    fetchOrders();
  }, [pagination.current, pagination.sortField, pagination.sortDirection]);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      // Calcul de la plage pour la pagination
      const from = (pagination.current - 1) * pagination.pageSize;
      const to = from + pagination.pageSize - 1;

      // Obtenir le nombre total
      const { count } = await supabase
        .from('lunch_orders')
        .select('*', { count: 'exact', head: true });

      // Obtenir les données paginées et triées
      const { data, error } = await supabase
        .from('lunch_orders')
        .select('*')
        .range(from, to)
        .order(pagination.sortField, { ascending: pagination.sortDirection === 'ascend' });

      if (error) throw error;

      setOrders(data);
      setPagination(prev => ({ ...prev, total: count }));
    } catch (error) {
      message.error('Erreur lors de la récupération des commandes : ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      const { error } = await supabase
        .from('lunch_orders')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      
      message.success('Commande supprimée avec succès');
      fetchOrders();
    } catch (error) {
      message.error('Erreur lors de la suppression de la commande : ' + error.message);
    }
  };

  const showEditModal = (order) => {
    setCurrentOrder(order);
    setIsEditing(true);
    editForm.setFieldsValue({
      ...order,
      date_of_reservation: dayjs(order.date_of_reservation),
      date_of_charter: dayjs(order.date_of_charter),
      pick_up_time: dayjs(order.pick_up_time, 'HH:mm:ss')
    });
  };

  const showViewDrawer = (order) => {
    setCurrentOrder(order);
    setIsViewDrawerOpen(true);
  };
  const handleAdd = async (values) => {
    try {
      const newOrder = {
        ...values,
        date_of_reservation: values.date_of_reservation.format('YYYY-MM-DD'),
        date_of_charter: values.date_of_charter.format('YYYY-MM-DD'),
        pick_up_time: values.pick_up_time.format('HH:mm:ss'),
        updated_at: new Date().toISOString() // Ajoutez le champ updated_at ici
      };
  
      const { error } = await supabase
        .from('lunch_orders')
        .insert([newOrder]);
  
      if (error) throw error;
  
      message.success('Commande ajoutée avec succès');
      form.resetFields();
      setIsAddDrawerOpen(false);
      fetchOrders();
    } catch (error) {
      message.error('Erreur lors de l\'ajout de la commande : ' + error.message);
    }
  };
  
  const handleUpdate = async (values) => {
    try {
      const updatedOrder = {
        ...values,
        date_of_reservation: values.date_of_reservation.format('YYYY-MM-DD'),
        date_of_charter: values.date_of_charter.format('YYYY-MM-DD'),
        pick_up_time: values.pick_up_time.format('HH:mm:ss'),
        updated_at: new Date().toISOString() // Ajoutez le champ updated_at ici
      };
  
      const { error } = await supabase
        .from('lunch_orders')
        .update(updatedOrder)
        .eq('id', currentOrder.id);
  
      if (error) throw error;
  
      message.success('Commande mise à jour avec succès');
      setIsEditing(false);
      fetchOrders();
    } catch (error) {
      message.error('Erreur lors de la mise à jour de la commande : ' + error.message);
    }
  };
  
  
  const columns = [
    {
      title: 'Nom du client',
      dataIndex: 'client_name',
      key: 'client_name',
      sorter: (a, b) => a.client_name.localeCompare(b.client_name)
    },
    {
      title: 'Description',
      dataIndex: 'description_title',
      key: 'description_title',
      sorter: (a, b) => a.description_title.localeCompare(b.description_title)
    },
    {
      title: 'Date de la sortie',
      dataIndex: 'date_of_charter',
      key: 'date_of_charter',
      render: (date) => dayjs(date).format('MM/DD/YYYY'),
      sorter: (a, b) => dayjs(a.date_of_charter).diff(dayjs(b.date_of_charter))
    },
    {
      title: 'Date de réservation',
      dataIndex: 'date_of_reservation',
      key: 'date_of_reservation',
      render: (date) => dayjs(date).format('MM/DD/YYYY'),
      sorter: (a, b) => dayjs(a.date_of_reservation).diff(dayjs(b.date_of_reservation))
    },
    {
      title: 'Heure de prise en charge',
      dataIndex: 'pick_up_time',
      key: 'pick_up_time',
      render: (time) => dayjs(time, 'HH:mm:ss').format('HH:mm'),
      sorter: (a, b) => dayjs(a.pick_up_time, 'HH:mm:ss').diff(dayjs(b.pick_up_time, 'HH:mm:ss'))
    },
    {
      title: 'Agence ou Hôtel',
      dataIndex: 'charterer_name',
      key: 'charterer_name',
      sorter: (a, b) => a.charterer_name.localeCompare(b.charterer_name)
    },
    {
      title: 'Fournisseur',
      dataIndex: 'provider_name',
      key: 'provider_name',
      sorter: (a, b) => a.provider_name.localeCompare(b.provider_name)
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button 
            type="primary" 
            ghost 
            icon={<EyeOutlined />} 
            onClick={() => showViewDrawer(record)}
          >
            Voir
          </Button>
          <Button 
            type="primary" 
            ghost 
            icon={<EditOutlined />} 
            onClick={() => showEditModal(record)}
          >
            Modifier
          </Button>
          <Popconfirm
            title="Supprimer la commande"
            description="Êtes-vous sûr de vouloir supprimer cette commande ?"
            onConfirm={() => handleDelete(record.id)}
            okText="Oui"
            cancelText="Non"
          >
            <Button danger icon={<DeleteOutlined />}>
              Supprimer
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  const OrderForm = ({ form, onFinish, submitText }) => (
    <Form
      form={form}
      onFinish={onFinish}
      layout="vertical"
    >
      <div className="grid grid-cols-2 gap-2">
        <Form.Item
          name="client_name"
          label="Nom du client"
          rules={[{ required: true, message: 'Le nom du client est requis' }]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="charterer_name"
          label="Agence ou Hôtel"
          rules={[{ required: true, message: 'L\'agence ou hôtel est requis' }]}
        >
          <Input />
        </Form.Item>
      </div>
  
      <div className="grid grid-cols-2 gap-2">
        <Form.Item
          name="date_of_reservation"
          label="Date de réservation"
          rules={[{ required: true, message: 'La date de réservation est requise' }]}
        >
          <DatePicker className="w-full" format="YYYY-MM-DD" />
        </Form.Item>
        <Form.Item
          name="date_of_charter"
          label="Date de la sortie"
          rules={[{ required: true, message: 'La date de sortie est requise' }]}
        >
          <DatePicker className="w-full" format="YYYY-MM-DD" />
        </Form.Item>
      </div>
  
      <div className="grid grid-cols-2 gap-2">
        <Form.Item
          name="pick_up_time"
          label="Heure de prise en charge"
          rules={[{ required: true, message: 'L\'heure est requise' }]}
        >
          <TimePicker className="w-full" format="HH:mm" />
        </Form.Item>
        <Form.Item
          name="provider_name"
          label="Fournisseur"
          rules={[{ required: true, message: 'Le fournisseur est requis' }]}
        >
          <Input />
        </Form.Item>
      </div>
  
      <Form.Item
        name="description_title"
        label="Description"
        rules={[{ required: true, message: 'La description est requise' }]}
      >
        <Input.TextArea rows={3} />
      </Form.Item>
  
      <Form.Item className="mb-0">
        <Button type="primary" htmlType="submit" className="w-full">
          {submitText}
        </Button>
      </Form.Item>
    </Form>
  );

  return (
    <div style={{ minHeight: '100vh', background: theme.colors.bgDark, padding: '20px' }} className="p-6">
      <Card className="mb-6">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold">Gestion des commandes de déjeuner</h1>
          <Button 
            type="primary" 
            icon={<PlusOutlined />} 
            onClick={() => setIsAddDrawerOpen(true)}
          >
            Ajouter une commande
          </Button>
        </div>
      </Card>

      <Card>
        <Table
          columns={columns}
          dataSource={orders}
          rowKey="id"
          loading={loading}
          pagination={{
            ...pagination,
            onChange: (page, pageSize) => {
              setPagination(prev => ({ ...prev, current: page, pageSize }));
            },
            onSortChange: (_, sorter) => {
              setPagination(prev => ({
                ...prev,
                sortField: sorter.field,
                sortDirection: sorter.order
              }));
            }
          }}
        />
      </Card>

      <Drawer
        title="Ajouter une nouvelle commande"
        placement="right"
        onClose={() => setIsAddDrawerOpen(false)}
        open={isAddDrawerOpen}
        width={400}
      >
        <OrderForm 
          form={form} 
          onFinish={handleAdd}
          submitText="Ajouter la commande"
        />
      </Drawer>

      <Drawer
        title="Détails de la commande"
        placement="right"
        onClose={() => setIsViewDrawerOpen(false)}
        open={isViewDrawerOpen}
        width={400}
      >
        {currentOrder && (
          <div>
            <p><strong>Nom du client :</strong> {currentOrder.client_name}</p>
            <p><strong>Description :</strong> {currentOrder.description_title}</p>
            <p><strong>Date de la sortie :</strong> {dayjs(currentOrder.date_of_charter).format('MM/DD/YYYY')}</p>
            <p><strong>Date de réservation :</strong> {dayjs(currentOrder.date_of_reservation).format('MM/DD/YYYY')}</p>
            <p><strong>Heure de prise en charge :</strong> {dayjs(currentOrder.pick_up_time, 'HH:mm:ss').format('HH:mm')}</p>
            <p><strong>Agence ou Hôtel :</strong> {currentOrder.charterer_name}</p>
            <p><strong>Fournisseur :</strong> {currentOrder.provider_name}</p>
            <div className="flex justify-end mt-4">
              <Button 
                type="primary" 
                ghost 
                icon={<EditOutlined />} 
                onClick={() => {
                  setIsViewDrawerOpen(false);
                  showEditModal(currentOrder);
                }}
              >
                Modifier
              </Button>
              <Popconfirm
                title="Supprimer la commande"
                description="Êtes-vous sûr de vouloir supprimer cette commande ?"
                onConfirm={() => {
                  setIsViewDrawerOpen(false);
                  handleDelete(currentOrder.id);
                }}
                okText="Oui"
                cancelText="Non"
              >
                <Button danger icon={<DeleteOutlined />} className="ml-2">
                  Supprimer
                </Button>
              </Popconfirm>
            </div>
          </div>
        )}
      </Drawer>

      <Modal
        title="Modifier la commande"
        open={isEditing}
        onCancel={() => setIsEditing(false)}
        footer={null}
      >
        <OrderForm 
          form={editForm} 
          onFinish={handleUpdate}
          submitText="Mettre à jour la commande"
        />
      </Modal>
    </div>
  );
};

export default LunchOrdersPage;